import { useState } from 'react'
import { Calendar, Copy, Share2, X } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { showToast } from './Toast'
import {
  buildReservationShareUrl,
  downloadReservationIcs,
  openGoogleCalendar,
  type ShareableReservation,
} from '../utils/reservationShare'

interface Props {
  open: boolean
  onClose: () => void
  reservation: ShareableReservation
  /**
   * #1291: 共有先 URL を上書きする。省略時は予約詳細 `/rsv/{shortCode}`。
   * 体験チケットは公開予約ページを持たないので店舗ページ URL を渡す。
   */
  shareUrl?: string
  /**
   * #1291: 日時が固定でない場合 (体験チケットの free_entry = フリー) のラベル。
   * 指定時は `{date} {time}` の代わりに表示し、カレンダー追加 (Google/.ics) は
   * 具体日時が無いため非表示にする。
   */
  scheduleLabel?: string
}

/**
 * 予約成立後の共有 sheet (#746).
 *
 * 共有経路 3 種:
 *  1. Web Share API (`navigator.share`) — OS 標準シェア → LINE / メール / 他
 *  2. URL コピー — fallback (Web Share 非対応 / desktop)
 *  3. .ics ダウンロード — Google / Apple カレンダーに「追加」できる
 *
 * 共有テキストには日時 / 店舗 / 人数 / コース / 短縮 URL が含まれる。
 * 詳細リンク先 (= `/r/{shortCode}`) は未ログインで PII 除外版が見える設計
 * (= グループ宴会の幹事以外のメンバー向け)。
 */
export default function ReservationShareSheet({
  open,
  onClose,
  reservation,
  shareUrl: shareUrlOverride,
  scheduleLabel,
}: Props) {
  const { t } = useI18n()
  const [busy, setBusy] = useState<'webshare' | 'copy' | 'ics' | 'gcal' | null>(null)

  if (!open) return null

  const shareUrl = shareUrlOverride ?? buildReservationShareUrl(reservation.shortCode)
  // #1291: 日時ラベルがある = フリー (時間指定なし) → カレンダー追加は不可
  const hasFixedTime = !scheduleLabel
  const scheduleDisplay = scheduleLabel ?? `${reservation.date} ${reservation.time}`.trim()

  // 共有テキスト本文 — i18n template (interpolation: storeName, date, time, partySize, course, url)
  const shareText = t('reservationShare.text', {
    storeName: reservation.storeName,
    date: scheduleLabel ?? reservation.date,
    time: scheduleLabel ? '' : reservation.time,
    partySize: String(reservation.partySize),
    course: reservation.courseName || t('reservationShare.noCourse'),
    url: shareUrl,
  })

  const handleWebShare = async () => {
    if (busy) return
    setBusy('webshare')
    try {
      // typeof は SSR 警戒 + 古い Edge / desktop 対策
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        try {
          await navigator.share({
            title: reservation.storeName,
            text: shareText,
            url: shareUrl,
          })
          // share UI が閉じた = success or user cancel。toast は出さない
          // (= 成功は OS 側で feedback、cancel を「失敗」と表示するのは UX 悪い)
          onClose()
        } catch (err) {
          // AbortError = user cancel → silent
          if ((err as Error)?.name !== 'AbortError') {
            console.warn('[ReservationShareSheet] navigator.share failed:', err)
            showToast({ type: 'error', message: t('reservationShare.webShareFailed') })
          }
        }
      } else {
        // Web Share API 非対応環境 → URL コピーに fallback
        await handleCopyUrl()
      }
    } finally {
      setBusy(null)
    }
  }

  const handleCopyUrl = async () => {
    if (busy) return
    setBusy('copy')
    try {
      // navigator.clipboard.writeText は HTTPS / localhost のみ。
      // それ以外 (= demo http?) では fallback として textarea + execCommand を使う。
      let copied = false
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(shareUrl)
          copied = true
        } catch {
          copied = false
        }
      }
      if (!copied && typeof document !== 'undefined') {
        const el = document.createElement('textarea')
        el.value = shareUrl
        el.setAttribute('readonly', '')
        el.style.position = 'absolute'
        el.style.left = '-9999px'
        document.body.appendChild(el)
        el.select()
        try {
          copied = document.execCommand('copy')
        } catch {
          copied = false
        }
        document.body.removeChild(el)
      }
      showToast({
        type: copied ? 'success' : 'error',
        message: t(copied ? 'reservationShare.copied' : 'reservationShare.copyFailed'),
      })
    } finally {
      setBusy(null)
    }
  }

  const handleOpenGoogleCalendar = () => {
    if (busy) return
    setBusy('gcal')
    try {
      openGoogleCalendar({ ...reservation, shareUrl })
    } finally {
      setBusy(null)
    }
  }

  const handleDownloadIcs = () => {
    if (busy) return
    setBusy('ics')
    try {
      downloadReservationIcs(
        {
          ...reservation,
          shareUrl,
        },
        `yokoso-${reservation.date}-${reservation.time.replace(':', '')}.ics`,
      )
      showToast({ type: 'success', message: t('reservationShare.icsDownloaded') })
    } finally {
      setBusy(null)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[430px] rounded-t-2xl bg-white p-6 pb-8 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
          aria-label="close"
        >
          <X size={18} className="text-text-secondary" />
        </button>

        <h3 className="font-display font-semibold text-lg text-text-primary mb-1">
          {t('reservationShare.title')}
        </h3>
        <p className="font-body text-xs text-text-tertiary mb-4">
          {reservation.storeName} · {scheduleDisplay}
        </p>

        <p className="font-body text-xs text-text-secondary mb-5">
          {t('reservationShare.description')}
        </p>

        <div className="space-y-2.5">
          <button
            onClick={handleWebShare}
            disabled={!!busy}
            className="tap-effect flex w-full h-12 items-center justify-center gap-2 rounded-xl bg-yokoso-red font-display text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            <Share2 size={16} />
            {t('reservationShare.shareViaApp')}
          </button>
          <button
            onClick={handleCopyUrl}
            disabled={!!busy}
            className="tap-effect flex w-full h-12 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white font-display text-sm font-semibold text-text-primary disabled:opacity-50"
          >
            <Copy size={16} className="text-text-secondary" />
            {t('reservationShare.copyUrl')}
          </button>
          {/* #1291: 具体日時があるときだけカレンダー追加を出す (フリー体験は非表示) */}
          {hasFixedTime && (
            <div className="grid grid-cols-2 gap-2.5">
              <button
                onClick={handleOpenGoogleCalendar}
                disabled={!!busy}
                className="tap-effect flex h-12 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white font-display text-sm font-semibold text-text-primary disabled:opacity-50"
              >
                <Calendar size={16} className="text-text-secondary" />
                {t('reservationShare.openGoogleCalendar')}
              </button>
              <button
                onClick={handleDownloadIcs}
                disabled={!!busy}
                className="tap-effect flex h-12 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white font-display text-sm font-semibold text-text-primary disabled:opacity-50"
              >
                <Calendar size={16} className="text-text-secondary" />
                {t('reservationShare.downloadIcs')}
              </button>
            </div>
          )}
        </div>

        <p className="mt-4 text-[10px] font-body text-text-tertiary leading-relaxed">
          {t('reservationShare.privacyNote')}
        </p>
      </div>
    </div>
  )
}
